import type { SourceInput } from '@markku/utils'
import { z } from 'zod'
import {
  TRACKED_CLICK_ID_KEYS,
  TRACKED_QUERY_KEYS,
  TRACKED_UTM_KEYS,
  type TrackedClickIdKey,
  type TrackedQueryKey,
  type TrackedUtmKey,
} from './acquisition-keys'

// Compile-time guard: every click ID in TRACKED_CLICK_ID_KEYS must also be a
// key on SourceInput in @markku/utils/acquisitionSource, so adding one to the
// canonical list but forgetting the deriveSource dispatch field becomes a
// type error rather than silent data loss. If this line fails to compile,
// the MissingClickIdFieldsInSourceInput alias resolves to the missing keys.
type MissingClickIdFieldsInSourceInput = Exclude<
  TrackedClickIdKey,
  keyof SourceInput
>
const _assertSourceInputCoversClickIds: [
  MissingClickIdFieldsInSourceInput,
] extends [never]
  ? true
  : false = true
void _assertSourceInputCoversClickIds

// Re-export the keys so existing @markku/types barrel imports keep working.
export {
  TRACKED_CLICK_ID_KEYS,
  TRACKED_QUERY_KEYS,
  TRACKED_UTM_KEYS,
  type TrackedClickIdKey,
  type TrackedQueryKey,
  type TrackedUtmKey,
}

// One schema field per click ID. `satisfies` forces TypeScript to error if
// TRACKED_CLICK_ID_KEYS gains or loses a key without a matching schema edit —
// that's the "single source of truth" linkage without giving up strong typing
// at read sites (deriveSource, reports, SQL queries).
const clickIdSchemaFields = {
  gclid: z.string().optional(),
  gclsrc: z.string().optional(),
  wbraid: z.string().optional(),
  gbraid: z.string().optional(),
  dclid: z.string().optional(),
  fbclid: z.string().optional(),
  msclkid: z.string().optional(),
  ttclid: z.string().optional(),
  twclid: z.string().optional(),
  li_fat_id: z.string().optional(),
  sccid: z.string().optional(),
  epik: z.string().optional(),
  rdt_cid: z.string().optional(),
} satisfies Record<TrackedClickIdKey, z.ZodOptional<z.ZodString>>

// Marketing attribution captured at a single touchpoint (page load / form submit).
export const acquisitionDataSchema = z.object({
  utmParams: z.record(z.string()).optional(),
  ...clickIdSchemaFields,
  referrer: z.string().optional(),
  landingPage: z.string().optional(),
  timestamp: z.string(),
  device: z
    .object({
      screenResolution: z.string(),
      language: z.string(),
      timeZone: z.string(),
    })
    .optional(),
})
export type AcquisitionData = z.infer<typeof acquisitionDataSchema>

// Persisted in the mk_attr cookie across sessions. firstTouch is write-once;
// lastTouch updates on each identifiable channel impression.
export const attributionPayloadSchema = z.object({
  firstTouch: acquisitionDataSchema,
  lastTouch: acquisitionDataSchema,
})
export type AttributionPayload = z.infer<typeof attributionPayloadSchema>
